import React from 'react';
import styled, { css } from 'styled-components';
import { useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { ISkillItem } from '@typings/store/skill.interface';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';

// ============================================
// 개별 아이템 로딩 컴포넌트들
// ============================================

/** 스토어 아이템 ID로 아이템 렌더링 */
function ItemById({
  id,
  size = 48,
  showName = true,
}: {
  id: string;
  size?: number;
  showName?: boolean;
}) {
  const { data } = useDocument<IstoreItem>(`${CollectionPath.STORE}/${id}`, {
    listen: false,
  });

  // 로딩 중이거나 데이터가 없는 경우 fallback UI 표시
  if (!data) {
    return (
      <RewardItemCard $showName={showName}>
        <FallbackImage $size={size}>
          <span>🎁</span>
        </FallbackImage>
        {showName && <RewardItemName>{id}</RewardItemName>}
      </RewardItemCard>
    );
  }

  return (
    <RewardItemCard $showName={showName}>
      <ItemImgCard data={data} style={{ width: size, height: size }} />
      {showName && <RewardItemName>{data.title}</RewardItemName>}
    </RewardItemCard>
  );
}

/** 스킬 ID로 스킬 렌더링 */
function SkillById({
  id,
  size = 48,
  showName = true,
}: {
  id: string;
  size?: number;
  showName?: boolean;
}) {
  const { data } = useDocument<ISkillItem>(`${CollectionPath.SKILLS}/${id}`, {
    listen: false,
  });

  if (!data) return null;

  return (
    <RewardItemCard $showName={showName}>
      <ItemImgCard data={data} style={{ width: size, height: size }} />
      {showName && <RewardItemName>{data.title}</RewardItemName>}
    </RewardItemCard>
  );
}

// ============================================
// 메인 RewardSection 컴포넌트
// ============================================

type RewardSectionProps = {
  /** 스토어 아이템 ID 배열 */
  itemIds?: string[];
  /** 스킬 ID 배열 */
  skillIds?: string[];
  /** 텍스트 보상 배열 (태그로 표시) */
  textRewards?: string[];
  /** 섹션 제목 (기본값: '주요 보상') */
  title?: string;
  /** 제목 표시 여부 (기본값: true) */
  showTitle?: boolean;
  /** 제목 스타일 타입: 'default' | 'divider' (기본값: 'default') */
  titleStyle?: 'default' | 'divider';
  /** 제목 폰트 크기 (기본값: '14px') */
  titleFontSize?: string;
  /** 아이템 이미지 크기 (기본값: 48) */
  itemSize?: number;
  /** 아이템 이름 표시 여부 (기본값: true) */
  showItemName?: boolean;
  /** 빈 상태 메시지 (기본값: '설정된 보상이 없습니다') */
  emptyMessage?: string;
  /** 빈 상태에서도 컴포넌트 표시 여부 (기본값: true) */
  showEmpty?: boolean;
  /** 커스텀 클래스명 */
  className?: string;
  /** 커스텀 스타일 */
  style?: React.CSSProperties;
  /** 아이템 그리드 gap (기본값: 12) */
  gap?: number;
  /** 컴팩트 모드 (이름 숨김, 작은 사이즈) */
  compact?: boolean;
};

function RewardSection({
  itemIds = [],
  skillIds = [],
  textRewards = [],
  title = '주요 보상',
  showTitle = true,
  titleStyle = 'default',
  titleFontSize = '14px',
  itemSize = 48,
  showItemName = true,
  emptyMessage = '설정된 보상이 없습니다',
  showEmpty = true,
  className,
  style,
  gap = 12,
  compact = false,
}: RewardSectionProps) {
  // 컴팩트 모드일 때 설정 오버라이드
  const finalItemSize = compact ? 32 : itemSize;
  const finalShowItemName = compact ? false : showItemName;
  const finalGap = compact ? 4 : gap;

  // 중복 제거
  const uniqueItemIds = Array.from(new Set(itemIds.filter(Boolean)));
  const uniqueSkillIds = Array.from(new Set(skillIds.filter(Boolean)));

  const hasItems = uniqueItemIds.length > 0;
  const hasSkills = uniqueSkillIds.length > 0;
  const hasTextRewards = textRewards && textRewards.filter(Boolean).length > 0;
  const isEmpty = !hasItems && !hasSkills && !hasTextRewards;

  // 빈 상태일 때 표시하지 않음
  if (isEmpty && !showEmpty) return null;

  return (
    <Container className={className} style={style}>
      {/* 제목 */}
      {showTitle && (
        titleStyle === 'divider' ? (
          <DividerContainer>
            <DividerLine />
            <DividerLabel $fontSize={titleFontSize}>{title}</DividerLabel>
            <DividerLine />
          </DividerContainer>
        ) : (
          <SectionTitle $fontSize={titleFontSize}>{title}</SectionTitle>
        )
      )}

      {/* 아이템 & 스킬 그리드 */}
      {(hasItems || hasSkills) && (
        <RewardItemsGrid $gap={finalGap}>
          {uniqueItemIds.map((itemId) => (
            <ItemById
              key={itemId}
              id={itemId}
              size={finalItemSize}
              showName={finalShowItemName}
            />
          ))}
          {uniqueSkillIds.map((skillId) => (
            <SkillById
              key={skillId}
              id={skillId}
              size={finalItemSize}
              showName={finalShowItemName}
            />
          ))}
        </RewardItemsGrid>
      )}

      {/* 텍스트 보상 태그 */}
      {hasTextRewards && (
        <RewardTagsContainer>
          {textRewards.filter(Boolean).map((reward, index) => (
            <RewardBadge key={index}>{reward}</RewardBadge>
          ))}
        </RewardTagsContainer>
      )}

      {/* 빈 상태 */}
      {isEmpty && showEmpty && <NoRewardsText>{emptyMessage}</NoRewardsText>}
    </Container>
  );
}

export default React.memo(RewardSection);

// ============================================
// Styled Components
// ============================================

const Container = styled.div`
  width: 100%;
`;

const SectionTitle = styled.h4<{ $fontSize?: string }>`
  color: var(--textColor);
  font-size: ${({ $fontSize }) => $fontSize || '14px'};
  font-weight: 600;
  margin: 0 0 12px 0;
`;

// Divider 스타일 (외부 의존성 제거)
const DividerContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0;
  gap: 12px;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: var(--lineColor);
`;

const DividerLabel = styled.span<{ $fontSize?: string }>`
  font-size: ${({ $fontSize }) => $fontSize || '14px'};
  color: var(--subTextColor);
  font-weight: 600;
  white-space: nowrap;
`;

const RewardItemsGrid = styled.div<{ $gap?: number }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ $gap }) => $gap || 12}px;
  margin-bottom: 12px;
`;

const showNameStyle = css`
  padding: 8px;
  background: var(--inkColor);
  border: 1px solid var(--lineColor);
  border-radius: 8px;
  min-width: 72px;
  max-width: 90px;
  transition: all 0.2s ease;
`;
const RewardItemCard = styled.div<{ $showName?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  ${({ $showName }) => $showName && showNameStyle}
  
  &:hover {
    border-color: var(--primaryColor);
    background: var(--primaryColor)10;
  }
`;

const RewardItemName = styled.span`
  font-size: 11px;
  color: var(--textColor);
  text-align: center;
  font-weight: 500;
  line-height: 1.3;
  word-break: keep-all;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const RewardTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// Badge 스타일 (외부 의존성 제거)
const RewardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  background: var(--inkColor);
  color: var(--textColor);
  border: 1px solid var(--lineColor);
`;

const NoRewardsText = styled.p`
  color: var(--subTextColor);
  font-size: 12px;
  font-style: italic;
  margin: 8px 0 0 0;
`;

const FallbackImage = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--inkColor);
  border: 1px dashed var(--lineColor);
  border-radius: 8px;
  
  span {
    font-size: ${({ $size }) => Math.max(16, $size * 0.4)}px;
  }
`;
